import type {useI18n} from '@/i18n'

type I18n = ReturnType<typeof useI18n>

export type TransformParamKind = 'text' | 'int' | 'list' | 'regex'

export interface TransformParamField {
    key: string
    label: string
    placeholder?: string
    required: boolean
    kind: TransformParamKind
}

/**
 * The parameter fields a given transform exposes. Mirrors the backend
 * {@code TransformArgsValidator} required-arg rules so the editor surfaces the
 * same shape (and the same save-time validation) the API enforces. Keyed by the
 * transform's API code so this shared helper stays free of entity-type imports.
 */
export function transformParamFields(transform: string | null, i18n: I18n): TransformParamField[] {
    if (!transform) {
        return []
    }
    const t = i18n.transformParams
    const zone: TransformParamField[] = [
        {key: 'zone', label: t.zone, placeholder: t.zonePlaceholder, required: false, kind: 'text'},
    ]
    const fields: Record<string, TransformParamField[]> = {
        TO_AD_GENERALIZED_TIME: zone,
        TO_WINDOWS_FILETIME: zone,
        PREFIX: [{key: 'value', label: t.value, required: true, kind: 'text'}],
        SUFFIX: [{key: 'value', label: t.value, required: true, kind: 'text'}],
        REPLACE: [
            {key: 'search', label: t.search, required: true, kind: 'text'},
            {key: 'replacement', label: t.replacement, required: true, kind: 'text'},
        ],
        SPLIT: [
            {key: 'delimiter', label: t.delimiter, required: true, kind: 'text'},
            {key: 'index', label: t.index, required: true, kind: 'int'},
        ],
        JOIN: [
            {key: 'sources', label: t.sources, placeholder: t.sourcesPlaceholder, required: true, kind: 'list'},
            {key: 'delimiter', label: t.delimiter, required: true, kind: 'text'},
        ],
        REGEX_EXTRACT: [
            {key: 'pattern', label: t.pattern, required: true, kind: 'regex'},
            {key: 'group', label: t.group, required: false, kind: 'int'},
        ],
        CONDITIONAL: [
            {key: 'equals', label: t.equals, required: true, kind: 'text'},
            {key: 'then', label: t.then, required: true, kind: 'text'},
            {key: 'else', label: t.elseValue, required: true, kind: 'text'},
        ],
    }
    return fields[transform] ?? []
}

/**
 * A one-line, example-bearing description of what the selected transform does.
 * Keyed by the transform's API code so this shared helper stays free of
 * entity-type imports, mirroring {@link transformParamFields}.
 */
export function transformDescription(transform: string | null, i18n: I18n): string | undefined {
    if (!transform) {
        return undefined
    }
    const d = i18n.transformParams.descriptions
    const map: Record<string, string> = {
        TO_AD_GENERALIZED_TIME: d.toAdGeneralizedTime,
        TO_WINDOWS_FILETIME: d.toWindowsFiletime,
        PREFIX: d.prefix,
        SUFFIX: d.suffix,
        REPLACE: d.replace,
        SPLIT: d.split,
        JOIN: d.join,
        REGEX_EXTRACT: d.regexExtract,
        CONDITIONAL: d.conditional,
        LOOKUP_ORG_UNIT: d.lookupOrgUnit,
        RESOLVE_MANAGER: d.resolveManager,
        TO_DATE: d.toDate,
        TO_UPPERCASE: d.toUppercase,
        TO_LOWERCASE: d.toLowercase,
    }
    return map[transform]
}

function isValidRegex(pattern: string): boolean {
    try {
        new RegExp(pattern)
        return true
    } catch (_e) {
        // RegExp throws on invalid syntax — that is exactly the condition we report.
        return false
    }
}

/** First inline validation error for the transform's args, or undefined when valid. */
export function validateTransformArgs(
    transform: string | null,
    args: Record<string, unknown> | null,
    i18n: I18n,
): string | undefined {
    for (const field of transformParamFields(transform, i18n)) {
        const raw = args?.[field.key]
        if (field.kind === 'list') {
            if (field.required && (!Array.isArray(raw) || raw.length === 0)) {
                return i18n.transformParams.requiredField({field: field.label})
            }
            continue
        }
        const value = raw == null ? '' : String(raw)
        if (field.required && value.trim() === '') {
            return i18n.transformParams.requiredField({field: field.label})
        }
        if (field.kind === 'int' && value.trim() !== '' && !/^-?\d+$/.test(value.trim())) {
            return i18n.transformParams.notInteger({field: field.label})
        }
        if (field.kind === 'regex' && value.trim() !== '' && !isValidRegex(value)) {
            return i18n.transformParams.invalidRegex
        }
    }
    return undefined
}
